/**
 * Second-factor setup.
 *
 * WHY IT HAS ITS OWN FILE NOW. This dialog used to be declared at the bottom of
 * `TopBar.tsx`, and the top bar's account menu was its only entry point in the
 * whole console. Deleting that file to build the design's single-column chrome
 * would have deleted second-factor enrolment with it, silently: nothing else
 * imports it, no route reaches it, no test named it, and the app would have
 * typechecked and built and shipped without a way to turn on an authenticator.
 * The API for it stays either way, so the loss would have looked like a feature
 * that never existed rather than one that was thrown out.
 *
 * So it is a component in its own right, and its entry point moved with the
 * rest of the account controls to the bottom of the sidebar.
 *
 * There is no QR library in this app, so the key and the setup link are shown
 * as text, which every authenticator accepts.
 */
import { useTotpEnroll } from '../lib/session'
import { Button } from './Button'
import { CodeBlock } from './CodeBlock'
import { Modal } from './Modal'
import { ErrorState } from './States'

export function TotpEnrollDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const enroll = useTotpEnroll()

  const start = () => {
    if (!enroll.data && !enroll.isPending) enroll.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set up an authenticator app"
      description="You will be asked for a six digit code from this app every time you sign in."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!enroll.data ? (
            <Button variant="primary" data-primary="" onClick={start} loading={enroll.isPending}>
              Generate a new code
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        {!enroll.data && !enroll.isPending && !enroll.error ? (
          <p className="text-sm leading-relaxed text-ink-muted">
            Generating a new code replaces any authenticator you already have set up, so only do this on
            a device you keep.
          </p>
        ) : null}

        {enroll.error ? <ErrorState error={enroll.error} onRetry={() => enroll.mutate()} /> : null}

        {enroll.data ? (
          <>
            <p className="text-sm leading-relaxed text-ink-muted">
              Add this to your authenticator app, either by pasting the setup link or by typing the key
              by hand. Keep it somewhere safe until you have signed in with it once.
            </p>
            <CodeBlock code={enroll.data.secret} title="Setup key" />
            <CodeBlock code={enroll.data.otpauthUri} title="Setup link" wrap />
          </>
        ) : null}
      </div>
    </Modal>
  )
}
